/**
 * What the device says about where the merchant is.
 *
 * A restaurant's currency, and the example phone and address it is shown, all
 * follow from the country it is in — so the guessing lives together rather than
 * spread across the screens that need it. Nothing here is conclusive: every
 * answer is a default the merchant can change.
 */


/**
 * ISO 3166 region to the currency we offer for it.
 *
 * Only regions whose currency is in `CURRENCIES` appear. A region that is
 * absent is genuinely unknown rather than unmapped: Switzerland uses CHF, which
 * we do not offer, so `CH` is better unanswered than answered wrongly.
 */
const REGION_CURRENCY: Record<string, string> = {
  SG: 'SGD',
  MY: 'MYR',
  ID: 'IDR',
  PH: 'PHP',
  TH: 'THB',
  HK: 'HKD',
  TW: 'TWD',
  JP: 'JPY',
  AU: 'AUD',
  US: 'USD',
  GB: 'GBP',
  IN: 'INR',
  // The eurozone, which shares one currency across twenty regions.
  AT: 'EUR', BE: 'EUR', CY: 'EUR', DE: 'EUR', EE: 'EUR', ES: 'EUR',
  FI: 'EUR', FR: 'EUR', GR: 'EUR', HR: 'EUR', IE: 'EUR', IT: 'EUR',
  LT: 'EUR', LU: 'EUR', LV: 'EUR', MT: 'EUR', NL: 'EUR', PT: 'EUR',
  SI: 'EUR', SK: 'EUR',
};

/**
 * Timezone to region, as a second opinion when the locale names no region.
 *
 * Only zones where the whole zone uses one currency: `Europe/Zurich` and
 * `America/Toronto` are deliberately absent, because those areas do not use a
 * currency we offer and a guess would be worse than none.
 */
const ZONE_REGION: Record<string, string> = {
  'Asia/Singapore': 'SG',
  'Asia/Kuala_Lumpur': 'MY',
  'Asia/Jakarta': 'ID',
  'Asia/Makassar': 'ID',
  'Asia/Jayapura': 'ID',
  'Asia/Pontianak': 'ID',
  'Asia/Manila': 'PH',
  'Asia/Bangkok': 'TH',
  'Asia/Hong_Kong': 'HK',
  'Asia/Taipei': 'TW',
  'Asia/Tokyo': 'JP',
  'Asia/Kolkata': 'IN',
  'Asia/Calcutta': 'IN',
  // Every Australian zone uses the Australian dollar.
  'Australia/Sydney': 'AU',
  'Australia/Melbourne': 'AU',
  'Australia/Brisbane': 'AU',
  'Australia/Adelaide': 'AU',
  'Australia/Perth': 'AU',
  'Australia/Darwin': 'AU',
  'Australia/Hobart': 'AU',
  'Australia/Canberra': 'AU',
  'Australia/Lord_Howe': 'AU',
};

/**
 * The region a BCP 47 locale names, for example `SG` from `en-SG`.
 *
 * @param locale A BCP 47 language tag.
 * @returns The uppercase region, or null when the tag names none.
 */
function regionFromLocale(locale: string): string | null {
  try {
    // `Intl.Locale` understands the tag properly; the trailing subtag is the
    // fallback for engines without it.
    const parsed = new Intl.Locale(locale);
    if (parsed.region) return parsed.region.toUpperCase();
  } catch {
    // An unparseable tag falls through to the subtag test below.
  }
  const parts = locale.split(/[-_]/);
  const last = parts[parts.length - 1];
  return parts.length > 1 && last !== undefined && /^[A-Za-z]{2}$/.test(last)
    ? last.toUpperCase()
    : null;
}

/**
 * The region the device appears to be in.
 *
 * The locale names one directly; the timezone stands in when it does not, which
 * is the case for a bare `en`.
 *
 * @param locale A BCP 47 language tag, typically `navigator.language`.
 * @param timeZone An IANA zone, typically from `Intl.DateTimeFormat`.
 * @returns An uppercase ISO 3166 region, or null when neither source knows.
 */
export function detectRegion(
  locale?: string | null,
  timeZone?: string | null,
): string | null {
  if (locale) {
    const region = regionFromLocale(locale);
    if (region && REGION_CURRENCY[region]) return region;
  }
  const zoneRegion = timeZone ? ZONE_REGION[timeZone] : undefined;
  return zoneRegion && REGION_CURRENCY[zoneRegion] ? zoneRegion : null;
}

/**
/**
 * An example phone number and address for a region.
 *
 * The phone example carries the international dialling prefix, which is the
 * part a merchant is most likely to get wrong. The address example is shaped
 * the way that country's postal system writes one — the point is the shape, not
 * the building.
 */
const REGION_CONTACT: Record<string, { phone: string; address: string }> = {
  SG: { phone: '+65 9123 4567', address: '42 Kandahar Street, Singapore 198900' },
  MY: { phone: '+60 12-345 6789', address: '12 Jalan Bukit Bintang, 55100 Kuala Lumpur' },
  ID: { phone: '+62 812-3456-7890', address: 'Jl. Jend. Sudirman No. 52, Jakarta 10220' },
  PH: { phone: '+63 917 123 4567', address: '123 Rizal Avenue, Makati City 1200' },
  TH: { phone: '+66 81 234 5678', address: '99 Sukhumvit Road, Bangkok 10110' },
  HK: { phone: '+852 9123 4567', address: "8 Queen's Road Central, Hong Kong" },
  TW: { phone: '+886 912 345 678', address: 'No. 100, Sec. 4, Zhongxiao E. Rd, Taipei 106' },
  JP: { phone: '+81 90-1234-5678', address: '1-1 Chiyoda, Chiyoda City, Tokyo 100-8111' },
  AU: { phone: '+61 412 345 678', address: '12 George Street, Sydney NSW 2000' },
  US: { phone: '+1 555 0100', address: '350 Fifth Avenue, New York, NY 10118' },
  GB: { phone: '+44 7700 900123', address: '10 Downing Street, London SW1A 2AA' },
  IN: { phone: '+91 98765 43210', address: '12 MG Road, Bengaluru, Karnataka 560001' },
  AT: { phone: '+43 664 1234567', address: 'Kärntner Straße 1, 1010 Wien' },
  BE: { phone: '+32 470 12 34 56', address: 'Rue Neuve 123, 1000 Bruxelles' },
  CY: { phone: '+357 96 123456', address: 'Ledra Street 10, 1011 Nicosia' },
  DE: { phone: '+49 151 23456789', address: 'Musterstraße 12, 10115 Berlin' },
  EE: { phone: '+372 5123 4567', address: 'Viru 5, 10111 Tallinn' },
  ES: { phone: '+34 612 345 678', address: 'Calle Mayor 10, 28013 Madrid' },
  FI: { phone: '+358 40 1234567', address: 'Mannerheimintie 10, 00100 Helsinki' },
  FR: { phone: '+33 6 12 34 56 78', address: '12 rue de Rivoli, 75001 Paris' },
  GR: { phone: '+30 691 234 5678', address: 'Ermou 20, 105 63 Athens' },
  HR: { phone: '+385 91 234 5678', address: 'Ilica 100, 10000 Zagreb' },
  IE: { phone: '+353 85 123 4567', address: '12 Grafton Street, Dublin D02 X285' },
  IT: { phone: '+39 312 345 6789', address: 'Via Roma 15, 20121 Milano' },
  LT: { phone: '+370 612 34567', address: 'Gedimino pr. 15, 01103 Vilnius' },
  LU: { phone: '+352 628 123 456', address: 'Grand-Rue 20, L-1660 Luxembourg' },
  LV: { phone: '+371 2123 4567', address: 'Brīvības iela 30, Rīga LV-1050' },
  MT: { phone: '+356 9912 3456', address: 'Republic Street 50, Valletta VLT 1111' },
  NL: { phone: '+31 6 12345678', address: 'Damrak 1, 1012 LG Amsterdam' },
  PT: { phone: '+351 912 345 678', address: 'Rua Augusta 100, 1100-053 Lisboa' },
  SI: { phone: '+386 31 234 567', address: 'Prešernova cesta 10, 1000 Ljubljana' },
  SK: { phone: '+421 901 234 567', address: 'Hlavné námestie 5, 811 01 Bratislava' },
};

/**
 * The countries a restaurant can be set up in, and the zones each one spans.
 *
 * Asia and Oceania only: the product's merchants are there, and every country
 * listed is one whose currency we offer. A country absent here is not
 * unsupported out of judgement — it simply has no currency in `CURRENCIES` yet,
 * and a country picker that cannot price in the local money helps nobody.
 */
export const COUNTRY_ZONES: Record<string, string[]> = {
  SG: ['Asia/Singapore'],
  MY: ['Asia/Kuala_Lumpur'],
  ID: ['Asia/Jakarta', 'Asia/Makassar', 'Asia/Jayapura'],
  PH: ['Asia/Manila'],
  TH: ['Asia/Bangkok'],
  HK: ['Asia/Hong_Kong'],
  TW: ['Asia/Taipei'],
  JP: ['Asia/Tokyo'],
  IN: ['Asia/Kolkata'],
  AU: [
    'Australia/Sydney',
    'Australia/Melbourne',
    'Australia/Brisbane',
    'Australia/Adelaide',
    'Australia/Perth',
    'Australia/Darwin',
    'Australia/Hobart',
  ],
};

/** The countries offered, in the order the picker shows them. */
export const COUNTRIES: string[] = Object.keys(COUNTRY_ZONES);

/**
 * The currency a country prices in.
 *
 * @param region An ISO 3166 region code.
 * @returns An ISO 4217 code, or null when the country is not offered.
 */
export function currencyForCountry(region: string | null): string | null {
  return region ? REGION_CURRENCY[region] ?? null : null;
}

/**
 * The zones a country spans.
 *
 * @param region An ISO 3166 region code.
 * @returns The zones, empty when the country is not offered.
 */
export function zonesForCountry(region: string | null): string[] {
  return region ? COUNTRY_ZONES[region] ?? [] : [];
}

/**
 * The city part of a zone, for a picker that shows one per line.
 *
 * `Australia/Sydney` reads as `Sydney`; the country is already chosen above it.
 *
 * @param zone An IANA zone.
 * @returns The city, or the zone unchanged when it has no area prefix.
 */
export function zoneLabel(zone: string): string {
  const city = zone.includes('/') ? zone.slice(zone.lastIndexOf('/') + 1) : zone;
  return city.replace(/_/g, ' ');
}

/**
 * A country's name in the reader's language.
 *
 * `Intl.DisplayNames` supplies this, so no country name is translated by hand
 * and a language added later is covered without touching this file.
 *
 * @param region An ISO 3166 region code.
 * @param locale A BCP 47 language tag; defaults to the runtime locale.
 * @returns The localised name, falling back to the code itself.
 */
export function countryName(region: string, locale?: string): string {
  try {
    const names = new Intl.DisplayNames(locale ? [locale] : undefined, { type: 'region' });
    return names.of(region) ?? region;
  } catch {
    return region;
  }
}

/**
 * The country the device appears to be in, limited to those we offer.
 *
 * @param locale A BCP 47 language tag, typically `navigator.language`.
 * @param timeZone An IANA zone, typically from `Intl.DateTimeFormat`.
 * @returns An offered region code, or null when the device says none.
 */
export function detectCountry(
  locale?: string | null,
  timeZone?: string | null,
): string | null {
  // The locale is the more deliberate signal, but only when it names a country
  // we offer. A merchant whose device is set to `en-US` while they stand in
  // Taipei is better served by their timezone than by their language settings,
  // so the zone is consulted whenever the locale's country is not one we can
  // set up in — not merely when the locale names no country at all.
  if (locale) {
    const region = regionFromLocale(locale);
    if (region && COUNTRY_ZONES[region]) return region;
  }

  const zoneRegion = timeZone ? ZONE_REGION[timeZone] : undefined;
  return zoneRegion && COUNTRY_ZONES[zoneRegion] ? zoneRegion : null;
}

/** Shown when the region is unknown or has no examples of its own. */
const FALLBACK_CONTACT = REGION_CONTACT.SG!;

/**
 * The example phone and address for a country.
 *
 * Keyed on the country the merchant chose, not on what the device implies: a
 * merchant in Singapore setting up a restaurant in Taiwan must be shown
 * Taiwanese examples, and inference answers Singapore from their locale.
 *
 * @param region An ISO 3166 region code.
 * @returns Placeholders for the phone and address fields.
 */
export function contactForCountry(region: string | null): {
  phonePlaceholder: string;
  addressPlaceholder: string;
} {
  const contact = (region ? REGION_CONTACT[region] : undefined) ?? FALLBACK_CONTACT;
  return { phonePlaceholder: contact.phone, addressPlaceholder: contact.address };
}
